import React from "react";

const PrivacyPolicyPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-900 text-white px-6 py-12">
      <div className="max-w-5xl mx-auto space-y-10">
        <h1 className="text-4xl font-bold text-blue-300 text-center">
          Privacy Policy
        </h1>

        <p className="text-gray-300 leading-relaxed">
          Keyway App understands that your privacy is important and that you
          care about how your personal data is used. We respect and value the
          privacy of everyone who uses our platform, and we are committed to
          protecting it in line with Nigerian Data Protection Regulation (NDPR)
          and relevant international standards.
        </p>

        {/* Section 1 */}
        <section>
          <h2 className="text-2xl font-semibold text-blue-200 mb-4">
            1. Definitions and Interpretation
          </h2>
          <ul className="list-disc list-inside text-gray-300 space-y-2">
            <li>
              <strong>Account</strong>: A user account required to access Keyway
              App services.
            </li>
            <li>
              <strong>Cookie</strong>: A small file stored on your device to
              enhance your experience.
            </li>
            <li>
              <strong>System</strong>: Keyway's mobile and web applications.
            </li>
            <li>
              <strong>We/Us/Our</strong>: Keyway App, operated by Business Yield
              Nigeria.
            </li>
          </ul>
        </section>

        {/* Section 2 */}
        <section>
          <h2 className="text-2xl font-semibold text-blue-200 mb-4">
            2. About Us
          </h2>
          <p className="text-gray-300">
            Keyway is a book summary App operated by Business Yield. Our offices
            are located at:
            <br />
            <span className="block mt-2">
              📍 House 4, C Crescent, Citec, Abuja, Nigeria
            </span>
            <span className="block">
              📍 10 Mast Street, London, United Kingdom
            </span>
          </p>
        </section>

        {/* Section 3 */}
        <section>
          <h2 className="text-2xl font-semibold text-blue-200 mb-4">
            3. Scope of This Policy
          </h2>
          <p className="text-gray-300">
            This policy only applies to your use of Keyway App and not to any
            third-party websites or services linked within it. We advise
            reviewing their privacy policies independently.
          </p>
        </section>

        {/* Section 4 */}
        <section>
          <h2 className="text-2xl font-semibold text-blue-200 mb-4">
            4. What Data We Collect
          </h2>
          <ul className="list-disc list-inside text-gray-300 space-y-2">
            <li>Name and contact details (email, phone)</li>
            <li>Location and property preferences</li>
            <li>Bank details and payment history</li>
            <li>Device and browser information</li>
            <li>Usage patterns and activity logs</li>
          </ul>
        </section>

        {/* Section 5 */}
        <section>
          <h2 className="text-2xl font-semibold text-blue-200 mb-4">
            5. How We Use Your Data
          </h2>
          <ul className="list-disc list-inside text-gray-300 space-y-2">
            <li>To manage your account and transactions</li>
            <li>To personalize your experience and recommend listings</li>
            <li>To send you updates, alerts, and service notifications</li>
            <li>To conduct analytics and improve our services</li>
            <li>To fulfill legal and regulatory obligations</li>
            <li>For customer support and dispute resolution</li>
          </ul>
        </section>

        {/* Section 6 */}
        <section>
          <h2 className="text-2xl font-semibold text-blue-200 mb-4">
            6. Data Storage & Security
          </h2>
          <p className="text-gray-300">
            Your data is securely stored on servers in Nigeria and the UK. We
            employ encryption, firewall protection, and access control to
            safeguard your data. We retain data only as long as necessary.
          </p>
        </section>

        {/* Section 7 */}
        <section>
          <h2 className="text-2xl font-semibold text-blue-200 mb-4">
            7. Sharing Your Data
          </h2>
          <p className="text-gray-300 mb-2">We may share your data with:</p>
          <ul className="list-disc list-inside text-gray-300 space-y-1">
            <li>Payment providers (e.g., Paystack, Flutterwave)</li>
            <li>Marketing partners (e.g., Meta, Google)</li>
            <li>Cloud service providers (e.g., AWS, Azure)</li>
            <li>Legal and government authorities if required</li>
          </ul>
        </section>

        {/* Section 8 */}
        <section>
          <h2 className="text-2xl font-semibold text-blue-200 mb-4">
            8. Business Transfers
          </h2>
          <p className="text-gray-300">
            In the event of a merger, acquisition, or sale of assets, user data
            may be transferred as part of the business assets.
          </p>
        </section>

        {/* Section 9–11 */}
        <section>
          <h2 className="text-2xl font-semibold text-blue-200 mb-4">
            9–11. Your Rights & Access to Data
          </h2>
          <ul className="list-disc list-inside text-gray-300 space-y-2">
            <li>Access or update your data via your account settings</li>
            <li>Withdraw consent or delete your account anytime</li>
            <li>Request a copy of the data we store about you</li>
            <li>Restrict or object to specific data processing</li>
          </ul>
        </section>

        {/* Section 12 */}
        <section>
          <h2 className="text-2xl font-semibold text-blue-200 mb-4">
            12. Cookies
          </h2>
          <p className="text-gray-300">
            Keyway App uses cookies to improve user experience, remember
            preferences, and track analytics. You can manage your cookie
            preferences in your browser settings.
          </p>
        </section>

        {/* Section 13 */}
        <section>
          <h2 className="text-2xl font-semibold text-blue-200 mb-4">
            13. Your Rights Under NDPR & GDPR
          </h2>
          <ul className="list-disc list-inside text-gray-300 space-y-2">
            <li>Right to be informed</li>
            <li>Right to access and data portability</li>
            <li>Right to object to processing</li>
            <li>Right to correct, delete or restrict your data</li>
            <li>Right to lodge a complaint with NITDA or other regulators</li>
          </ul>
        </section>

        {/* Section 14 */}
        <section>
          <h2 className="text-2xl font-semibold text-blue-200 mb-4">
            14. Automated Decisions
          </h2>
          <p className="text-gray-300">
            We do not make automated decisions with legal effect. If we
            implement such systems, you have the right to human intervention and
            explanation.
          </p>
        </section>

        {/* Section 15 */}
        <section>
          <h2 className="text-2xl font-semibold text-blue-200 mb-4">
            15. Contacting Us
          </h2>
          <p className="text-gray-300 mb-2">
            Reach out to us via any of the channels below:
          </p>
          <ul className="text-gray-300">
            <li>
              <strong>Call:</strong> +44 740 041 2112 | +234 704 390 7787
            </li>
            <li>
              <strong>Email:</strong>{" "}
              <a
                className="text-blue-400 underline"
                href="mailto:contact@businessyield.com"
              >
                contact@businessyield.com
              </a>
            </li>
            <li>
              <strong>Office:</strong>
            </li>
            <li className="ml-4">📍 House 4, C Crescent, Citec, Abuja</li>
            <li className="ml-4">📍 10 Mast Street, London, UK</li>
          </ul>
        </section>

        {/* Section 16 */}
        <section>
          <h2 className="text-2xl font-semibold text-blue-200 mb-4">
            16. Updates to This Policy
          </h2>
          <p className="text-gray-300">
            We may update this policy from time to time. All updates will be
            posted on this page and effective immediately upon publishing.
          </p>
        </section>

        {/* Support */}
        <section>
          <h2 className="text-2xl font-semibold text-blue-200 mb-4">
            Reliable Customer Support
          </h2>
          <p className="text-gray-300">
            Customer support is our No. 1 priority. Whether it's night or
            weekend, our team will respond as soon as possible.
          </p>
        </section>

        <footer className="border-t border-gray-700 pt-6 mt-12 text-sm text-center text-gray-500">
          &copy; {new Date().getFullYear()} Keyway App – All Rights Reserved.
        </footer>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
